import React, { useState, useEffect } from 'react';
import {
    MapPin,
    Building,
    MoreVertical,
    Edit,
    Phone,
    Mail,
    Trash2
} from 'lucide-react';

const Entreprise = ({ searchKey, idCustomer }) => {
    const [entreprises, setEntreprises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);

    // Fonction pour récupérer les entreprises
    const fetchEntreprises = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch('https://search.mg.formafusion.io/api/search/entreprises', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    key: searchKey,
                    idCustomer: idCustomer,
                }),
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const data = await response.json();
            setEntreprises(data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (searchKey && idCustomer) {
            fetchEntreprises();
        }
    }, [searchKey, idCustomer]);

    // Handlers pour les actions
    const editClient = (id) => {
        // Logique d'édition
        setDropdownOpen(null);
    };

    const deleteClient = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
            try {
                // Logique de suppression
                // Actualiser la liste après suppression
                await fetchEntreprises();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
            }
        }
        setDropdownOpen(null);
    };

    const showCustomer = (id, path) => {
        // Logique d'affichage
    };

    const toggleDropdown = (id) => {
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = () => {
            setDropdownOpen(null);
        };

        if (dropdownOpen) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [dropdownOpen]);

    // Composant de chargement
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-b-2 border-purple-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    // Composant d'erreur
    if (error) {
        return (
            <div className="p-4 text-center text-red-500">
                <p>Erreur: {error}</p>
                <button
                    onClick={fetchEntreprises}
                    className="px-4 py-2 mt-2 text-white bg-purple-600 rounded hover:bg-purple-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    // Aucun client trouvé
    if (!entreprises || entreprises.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500">
                <Building className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>Aucun client trouvé pour "{searchKey}".</p>
            </div>
        );
    }

    return (
        <div className="container p-6 mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-800">
                    Vous avez {entreprises.length} Client{entreprises.length > 1 ? 's' : ''}
                </h1>
                <button
                    onClick={fetchEntreprises}
                    className="px-4 py-2 text-white transition-colors bg-purple-600 rounded-lg hover:bg-purple-700"
                >
                    Actualiser
                </button>
            </div>

            <section className="mt-4">
                <div className="grid grid-cols-1 gap-6 2xl:grid-cols-3 lg:grid-cols-3 md:grid-cols-2">
                    {entreprises.map((etp) => (
                        <div key={etp.idEtp} className="overflow-hidden transition-shadow duration-200 bg-white shadow-lg rounded-xl hover:shadow-xl">
                            <div className="flex flex-col p-4 h-96">
                                {/* En-tête avec logo et menu */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-16 h-16 overflow-hidden bg-gray-100 rounded-lg">
                                            {etp.etp_logo ? (
                                                <img
                                                    src={`https://formafusionmg.ams3.cdn.digitaloceanspaces.com/formafusionmg/img/modules/${etp.etp_logo}`} 
                                                    alt={`Logo ${etp.etp_name}`}
                                                    className="object-cover w-full h-full"
                                                />
                                            ) : (
                                                <Building className="w-8 h-8 text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold leading-tight text-gray-800">
                                                {etp.etp_name}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                {/* Contenu principal */}
                                <div className="flex-1 p-4 rounded-lg bg-slate-50">
                                    {/* Référent */}
                                    <div className="mb-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <span className="text-sm text-gray-500">Référent</span>
                                                <p className="font-medium text-gray-700">
                                                    {etp.etp_referent_name} {etp.etp_referent_firstname}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Informations de contact */}
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Mail className="flex-shrink-0 w-4 h-4 text-gray-500" />
                                            <span className="text-sm text-gray-600 truncate">
                                                {etp.etp_email || 'Non renseigné'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone className="flex-shrink-0 w-4 h-4 text-gray-500" />
                                            <span className="text-sm text-gray-600">
                                                {etp.etp_phone || 'Non renseigné'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <MapPin className="flex-shrink-0 w-4 h-4 text-gray-500" />
                                            <span className="text-sm text-gray-600 truncate">
                                                {etp.etp_addr_lot || 'Non renseigné'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Entreprise;